/**
 * Client-side company tenant URL utilities (mirrors auth-service companyUrl.ts).
 */

export type AppEnvironment = "local" | "development" | "staging" | "production";

const BASE_DOMAIN = "criticalasset.com";

export function detectAppEnvironment(hostname?: string): AppEnvironment {
  const host = (hostname ?? (typeof window !== "undefined" ? window.location.hostname : "")).toLowerCase();
  if (!host || host === "localhost" || host === "127.0.0.1") {
    return "local";
  }
  if (host.includes("dev") || host.includes("development")) {
    return "development";
  }
  if (host.includes("stg") || host.includes("staging")) {
    return "staging";
  }
  return "production";
}

export function getAppEnvironment(): AppEnvironment {
  return detectAppEnvironment();
}

export interface BuildCompanyFrontendUrlOptions {
  path?: string;
  env?: AppEnvironment;
  protocol?: string;
}

export function buildCompanyFrontendUrl(
  subdomain: string,
  options: BuildCompanyFrontendUrlOptions = {}
): string {
  const slug = subdomain.toLowerCase().trim();
  const env = options.env ?? getAppEnvironment();
  const protocol = options.protocol ?? (env === "local" ? "http" : "https");

  let host: string;
  if (env === "local") {
    host = `${slug}.localhost:3005`;
  } else if (env === "development") {
    host = `${slug}.dev.${BASE_DOMAIN}`;
  } else if (env === "staging") {
    host = `${slug}.stg.${BASE_DOMAIN}`;
  } else {
    host = `${slug}.beta.${BASE_DOMAIN}`;
  }

  const path = options.path ? (options.path.startsWith("/") ? options.path : `/${options.path}`) : "";
  return `${protocol}://${host}${path}`;
}

/** Fallback when company has no subdomain */
export function getGenericCompanyPortalLoginUrl(): string {
  const env = getAppEnvironment();
  if (env === "local") return "http://localhost:3005/login";
  if (env === "development") return `https://company-dev.${BASE_DOMAIN}/login`;
  if (env === "staging") return `https://company-stg.${BASE_DOMAIN}/login`;
  return `https://beta.${BASE_DOMAIN}/login`;
}

export function buildCompanySwitchLoginUrl(
  subdomain: string | null | undefined,
  queryParams?: Record<string, string>
): string {
  const base = subdomain
    ? buildCompanyFrontendUrl(subdomain, { path: "/login" })
    : getGenericCompanyPortalLoginUrl();

  if (!queryParams || Object.keys(queryParams).length === 0) {
    return base;
  }

  const url = new URL(base);
  for (const [key, value] of Object.entries(queryParams)) {
    if (value) url.searchParams.set(key, value);
  }
  return url.toString();
}

export function companyPortalLoginUrlForUser(user?: {
  subdomain?: string | null;
  email?: string | null;
} | null): string {
  const email = user?.email || undefined;
  return buildCompanySwitchLoginUrl(
    user?.subdomain,
    email ? { email } : undefined
  );
}
